import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "@helpers/translate";
import { Share } from "../../Orders/Share";
import { formatDollarPrice, toNumber } from "../../Orders/positionFormat";
import { TradeModal } from "../TradeModal";
import { TradeDetailsHeader } from "./components/TradeDetailsHeader";
import { TradeReplayChart } from "./components/TradeReplayChart";
import { TradeResult } from "./components/TradeResult";
import { TradeStats } from "./components/TradeStats";
import { TradeMeta } from "./components/TradeMeta";
import { TradeSummary } from "./components/TradeSummary";
import { CHART_VIEW } from "./constants";
import { useChartHover } from "./hooks/useChartHover";
import { useTradeCandles } from "./hooks/useTradeCandles";
import { useTradeReplay } from "./hooks/useTradeReplay";
import { getChartView } from "./utils/chartView";
import { formatTimeHeld } from "./utils/tradeMath";
import { toShareOrder } from "./utils/tradeModel";
import "./TradeDetailsModal.sass";

const TITLE_ID = "trade-details-title";

const TradeDetailsContent = ({ trade, livePrice, onClose, onUserClick }) => {
  const { __ } = useTranslation();
  const [isShareOpen, setIsShareOpen] = useState(false);
  // Someone else's open trade follows the market price of its pair
  const tickerPrice = useSelector((state) =>
    toNumber(state.futures.tickersMap?.[trade.symbol.toLowerCase()]?.lastPrice),
  );
  const currentPrice = trade.isClosed ? null : livePrice ?? tickerPrice;
  const endTime = trade.closedAt ?? Date.now();
  const view = getChartView(trade.openedAt, endTime);
  const isSummary = view === CHART_VIEW.SUMMARY;
  const { status, candles, entryIndex, intervalMs, retry } = useTradeCandles(
    trade,
    !isSummary,
  );
  const replay = useTradeReplay({
    trade,
    candles,
    entryIndex,
    intervalMs,
    livePrice: currentPrice,
    isSmooth: view === CHART_VIEW.LINE,
  });
  const { hover, point, setHoveredIndex } = useChartHover({
    trade,
    candles: replay.rangeCandles,
    intervalMs,
    isEnabled: replay.isDone,
  });
  // The hovered point, else where the replay is
  const shown = point ?? replay;
  const isProfit = (replay.pnl ?? 0) >= 0;
  const timeHeld = formatTimeHeld(trade.openedAt, endTime);
  const header = (
    <TradeDetailsHeader
      titleId={TITLE_ID}
      coin={trade.coin}
      coinIcon={trade.coinIcon}
      // Without a chart the coin's name, with it the price it plays at
      subtitle={
        isSummary ? trade.coinName ?? trade.coin : formatDollarPrice(shown.price)
      }
      isClosed={trade.isClosed}
      onClose={onClose}
    />
  );

  return (
    <TradeModal
      isOpen
      onClose={onClose}
      className={`trade-details${isSummary ? " trade-details--summary" : ""}`}
      labelledBy={TITLE_ID}
    >
      {isSummary ? (
        <>
          {header}
          <TradeSummary
            trade={trade}
            pnl={replay.pnl}
            multiplier={replay.multiplier}
            timeHeld={timeHeld}
          />
        </>
      ) : (
        <div className="trade-details__card">
          {header}
          <TradeReplayChart
            view={view}
            status={status}
            candles={replay.candles}
            rangeCandles={replay.rangeCandles}
            revealed={replay.revealed}
            entryPrice={trade.entryPrice}
            exitPrice={trade.isClosed ? trade.exitPrice : null}
            entryTime={trade.openedAt}
            exitTime={trade.closedAt}
            intervalMs={intervalMs}
            isDone={replay.isDone}
            isProfit={isProfit}
            isLong={trade.isLong}
            hover={hover}
            onHoverChange={setHoveredIndex}
            onRetry={retry}
          />
          <TradeResult
            pnl={shown.pnl}
            multiplier={shown.multiplier}
            isDemo={trade.isDemo}
          />
          <TradeStats
            wager={trade.wager}
            leverage={trade.leverage}
            timeHeld={timeHeld}
          />
        </div>
      )}

      <TradeMeta
        user={trade.user}
        openedAt={isSummary ? trade.openedAt : null}
        tradeId={trade.tradeId}
        onUserClick={onUserClick}
      />

      <div className="trade-details__actions">
        <button
          type="button"
          className="trade-details__share"
          onClick={() => setIsShareOpen(true)}
        >
          {__("futures.trade_details.share")}
        </button>
        <button
          type="button"
          className="trade-details__close"
          onClick={onClose}
        >
          {__("futures.trade_modals.close")}
        </button>
      </div>

      <Share
        active={isShareOpen}
        setActive={setIsShareOpen}
        order={toShareOrder(trade, replay.price)}
        pnl={replay.pnl != null ? replay.pnl.toFixed(2) : "--"}
        markPrice={replay.price}
        placedBy={trade.shareOrder ? null : trade.user}
      />
    </TradeModal>
  );
};

/**
 * Details of a trade, own or someone else's, open or closed: its candles
 * played from the entry with the price, P&L and multiplier at each step.
 *
 * @param {object} props
 * @param {import("./utils/tradeModel").TradeDetails|null} props.trade shown while set
 * @param {number|null} [props.livePrice] mark price of an own open trade
 * @param {() => void} props.onClose
 * @param {(user: string) => void} [props.onUserClick] opens the trader profile
 */
export const TradeDetailsModal = ({ trade, ...props }) =>
  trade ? <TradeDetailsContent trade={trade} {...props} /> : null;
